import { nodes, links, labelOf, labelOfId, MODES } from "../../data/decomposition";

// The drawing, as text. This is the whole point of the accessibility promise:
// a canvas cannot be read, so everything the drawing states has to exist here
// too - not a summary of it, and not the old marketing site, which was never
// generated from this data and could drift from it silently.
//
// It is built from the same `nodes` and `links` the plate renders, so it
// cannot fall out of step. If a node is on the map it is on this page, with
// its role, its note, what it sits inside, what it contains, who it connects
// to, and every link it owns.
//
// Headings are one per part, so a screen reader can jump through the system
// part by part instead of walking a flat list.

const MODE_NAME = {
  [MODES.INTERFACE]: "interfaces with",
  [MODES.PRODUCED_BY]: "produced",
  [MODES.QUERY]: "relationship unresolved with",
  [MODES.OFFSHOOT]: "has an offshoot",
  [MODES.CONTAINS]: "contains",
};

const LINK_LABEL = {
  repo: "Source",
  live: "Live site",
  doi: "DOI",
  paper: "Paper",
  dist: "Package",
};

// A node's relationships in both directions, phrased from that node's side so
// each line reads as a sentence rather than a row of a table.
function relationsFor(id) {
  const out = [];
  links.forEach((l) => {
    if (l.source === id) out.push(`${MODE_NAME[l.mode] || "relates to"} ${labelOfId(l.target)}`);
    else if (l.target === id) {
      out.push(l.mode === MODES.PRODUCED_BY
        ? `produced by ${labelOfId(l.source)}`
        : `${MODE_NAME[l.mode] || "relates to"} ${labelOfId(l.source)}`);
    }
  });
  return out;
}

export default function ReadableSheet({ reason }) {
  const childrenOf = (id) => nodes.filter((n) => n.parent === id).map(labelOf);

  return (
    <main className="plate-readable">
      <h1>Liz Osborn — system decomposition, in text</h1>

      <p>
        {reason
          ? "This is the readable version of the drawing, shown because the viewport is narrow, motion is reduced, or WebGL is unavailable."
          : "This is the readable version of the drawing on the front page."}{" "}
        It is generated from the same data the drawing is, so everything on the
        map is here: what each part is, what it sits inside, what it connects
        to, and every link it has.
      </p>

      <nav aria-label="Other pages">
        <ul>
          <li><a href="/">The drawing</a></li>
          <li><a href="/cv">Curriculum vitae</a></li>
          <li><a href="/notes">Notes</a></li>
        </ul>
      </nav>

      <p>{nodes.length} parts.</p>

      {nodes.map((n) => {
        const kids = childrenOf(n.id);
        const rel = relationsFor(n.id);
        const linkEntries = Object.entries(n.links || {});
        return (
          <section key={n.id} aria-labelledby={`r-${n.id}`}>
            <h2 id={`r-${n.id}`}>{labelOf(n)}</h2>
            <dl>
              <dt>Kind</dt>
              <dd>
                {n.kind}
                {n.priv ? ", closed source" : ""}
                {n.unknown ? ", placement unresolved" : ""}
                {n.standalone ? ", no relationship established yet" : ""}
              </dd>
              {n.role ? (<><dt>Role</dt><dd>{n.role}</dd></>) : null}
              {n.note ? (<><dt>Note</dt><dd>{n.note}</dd></>) : null}
              {n.parent ? (<><dt>Inside</dt><dd>{labelOfId(n.parent)}</dd></>) : null}
              {kids.length ? (<><dt>Contains</dt><dd>{kids.join(", ")}</dd></>) : null}
              {rel.length ? (<><dt>Connections</dt><dd>{rel.join("; ")}</dd></>) : null}
              {linkEntries.length ? (
                <>
                  <dt>Links</dt>
                  <dd>
                    <ul>
                      {linkEntries.map(([k, href]) => (
                        <li key={k}>
                          <a href={href}>{LINK_LABEL[k] || k}</a>
                        </li>
                      ))}
                    </ul>
                  </dd>
                </>
              ) : null}
            </dl>
          </section>
        );
      })}
    </main>
  );
}
